import { formatDate } from '../../utils/format';
import type { PrintData } from '../../utils/printService';
import type { RegistrationFormData, RegistrationPerson } from '../../api/endpoints';

// A family registration as print-template data — the fields Settings › Print
// Templates offers for the "Family Registration Form" document
// (printDocuments.ts). Used by the public page and the office's review alike.

export const REGISTRATION_PRINT_MODULE = 'Members & Families';
export const REGISTRATION_PRINT_DOCUMENT = 'FAMILY_REGISTRATION';

const STATUS_LABEL: Record<string, string> = {
  PENDING: 'Waiting for approval',
  REJECTED: 'Not approved',
  APPROVED: 'Approved'
};

const when = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const date = (v?: string) => (v ? formatDate(v, '') : '');

/** Printed documents never carry a full Aadhaar number. */
const maskAadhaar = (v?: string) => {
  const digits = (v || '').replace(/\D/g, '');
  return digits.length >= 4 ? `XXXX XXXX ${digits.slice(-4)}` : '';
};

/** The registration's own details (whatever its source). */
export interface RegistrationPrintInfo {
  referenceNo?: string;
  status?: string;
  submittedAt?: string;
  approvedAt?: string;
  rejectReason?: string;
  familyId?: string;
  membershipNo?: string;
}

const personRow = (p: RegistrationPerson, index: number, isHead: boolean): PrintData => ({
  index,
  name: p.name,
  relationToHead: isHead ? 'Head' : p.relationToHead || '',
  gender: p.gender || '',
  dateOfBirth: date(p.dateOfBirth),
  maritalStatus: p.maritalStatus || '',
  mobileNumber: p.mobileNumber || '',
  memberStatus: p.memberStatus || '',
  relativeName: p.relativeName || '',
  dateOfMarriage: date(p.dateOfMarriage),
  bloodGroup: p.bloodGroup || '',
  education: p.education || '',
  occupation: p.occupation || '',
  aadhaarNumber: maskAadhaar(p.aadhaarNumber),
  location: p.location || '',
  barcodeMembershipNo: p.barcodeMembershipNo || '',
  lifeStatus: p.status || 'Alive',
  dateOfDeath: date(p.dateOfDeath),
  transferChurch: p.transferChurch || '',
  guestFrom: date(p.guestFrom),
  guestUntil: date(p.guestUntil),
  baptismDate: date(p.baptismDate),
  confirmationDate: date(p.confirmationDate),
  communionStatus: p.communionStatus || ''
});

export const registrationPrintData = (info: RegistrationPrintInfo, form: RegistrationFormData): PrintData => {
  const members = form.members || [];
  const rows = [personRow(form.head, 1, true), ...members.map((m, i) => personRow(m, i + 2, false))];
  return {
    referenceNo: info.referenceNo || '',
    registrationStatus: STATUS_LABEL[info.status || ''] || info.status || '',
    submittedAt: when(info.submittedAt),
    approvedAt: when(info.approvedAt),
    rejectReason: info.status === 'REJECTED' ? info.rejectReason || '' : '',
    familyId: info.familyId || '',
    membershipNo: info.membershipNo || '',
    headOfFamily: form.head.name,
    headMobile: form.head.mobileNumber || '',
    headDateOfBirth: date(form.head.dateOfBirth),
    address: form.address || '',
    parish: form.parish || '',
    email: form.email || '',
    memberCount: rows.length,
    rowCount: rows.length,
    rows
  };
};
